import React from 'react';
import { useLocation } from 'react-router-dom';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { GRADIENT_SIDEBAR } from '@parsina/framework/tokens';
import { logout } from '../../lib/auth.js';
import { brandAsset } from '../../lib/brandAssets';
import { useCreditPurchase } from '../../hooks/useCreditPurchase';
import { AccountCard } from '@parsina/framework';
import { useDoctor } from '@parsina/framework/clinical/src/hooks/useDoctor';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useWalletBalance } from '@parsina/framework/clinical/src/hooks/useWalletBalance';
import { creditLevel, CREDIT_LEVEL_META } from '@parsina/framework/clinical/src/constants/credit';
import { fmtCoins } from '@parsina/framework/clinical/src/constants/creditPricing';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { accountUrl } from '../../lib/runtimeEnv';
import { useChatUnread } from '../../lib/chatRuntime';
import { useClinicSwitch, enterClinicPanel } from '../../hooks/useClinicSwitch';
import { formatUnread } from '../../screens/Messages/Messages.shared';
import { Globe } from 'lucide-react';
import { localMyPageString, hasLocalMyPageString } from '../../screens/MyPage/myPageStrings';
import SecretaryHost, { SecretaryProvider } from '../../secretary/SecretaryHost';
import SecretaryRecorder from '../../secretary/SecretaryRecorder';

// `labelKey` rather than a label: the sidebar is chrome that has to follow the
// UI language, and this list is module-level so it cannot call a hook itself.
const NAV_ITEMS = [
  {
    // پذیرش — the clipboard-with-patient reads as an intake desk; the old
    // grid-of-squares was the generic "dashboard" glyph and no longer fits.
    id: 'reception', labelKey: 'nav.items.reception',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 4c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm6 12H6v-1.4c0-2 4-3.1 6-3.1s6 1.1 6 3.1V19z"/></svg>,
  },
  {
    id: 'encounters', labelKey: 'nav.items.encounters',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>,
  },
  {
    // پیام‌ها — the speech bubble with a tail, the one glyph nobody has to
    // learn. `badge: 'chat'` marks this as the row that carries the live unread
    // chip (see the nav loop); no other item has a counter today.
    id: 'messages', labelKey: 'nav.items.messages', badge: 'chat',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM6 7h12v2H6zm0 4h8v2H6z"/></svg>,
  },
  {
    id: 'accounting', labelKey: 'nav.items.accounting',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>,
  },
  {
    // «صفحهٔ من» — the doctor's public page (docs/DOCTOR_PUBLIC_PAGE_DESIGN.md §12).
    // Its label is app-local until the kit catalogue carries it (see `navLabel`).
    id: 'my-page', labelKey: 'nav.items.myPage',
    icon: <Globe size={20} strokeWidth={1.8} aria-hidden />,
  },
  // «اعتبار هوشمند» is deliberately NOT a nav item: /credits is entered from the
  // always-visible balance chip in the doctor card below (and پذیرش/settings
  // links) — the balance needs persistent visibility, the report page does not.
  {
    id: 'profile', labelKey: 'nav.items.profile',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>,
  },
];

// Glyphs for the AccountCard menu. App-local, like NAV_ITEMS above: the kit owns
// the menu's shape, not its destinations, so it owns none of their icons either.
const ICON = {
  account: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>,
  coin: <svg viewBox="0 0 24 24" fill="none" width="17" height="17"><circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8"/><path d="M12 7.5v9M9.5 10h5M9.5 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
  clinic: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/></svg>,
  // Not mirrored: the arrow points out of a door, not along the reading direction.
  logout: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>,
};

/*
 * RTL layout: sidebar is on the RIGHT side of the screen.
 * When sidebar collapses, overflow:hidden clips from the LEFT.
 * So anything that must stay visible (toggle button, icons) must be
 * anchored to the RIGHT — i.e., come FIRST in RTL flex order.
 */
const MINI = 68;  // collapsed width
const FULL = 260; // expanded width

// Collapsing the sidebar is a PREFERENCE, not page state. It used to be plain
// component state, so it was reset by anything that remounted the shell — a
// reload, a token refresh, a bounce through a shell-less route — and the doctor
// had to re-collapse it every time.
//
// One page is the exception, by decision: the Visit workspace OPENS with the
// rail collapsed, because it needs every column it can get. That is a starting
// state, not a write — the doctor can expand it for this visit, the stored
// preference is never touched from there, and leaving the visit puts back
// exactly what they had chosen. The next visit opens collapsed again.
const SIDEBAR_KEY = 'parsina.sidebar.collapsed';

const isVisitPath = (pathname) => pathname.startsWith('/visit/');

const readSidebarPref = () => {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    // Private mode / storage disabled — expanded is the safe default.
    return false;
  }
};

const writeSidebarPref = (value) => {
  try {
    localStorage.setItem(SIDEBAR_KEY, value ? '1' : '0');
  } catch { /* preference simply does not persist */ }
};

const WebLayout = ({ children }) => {
  const { t, has, locale, formatNumber } = useTranslation();
  // A nav label the kit catalogue does not carry yet falls back to its app-local table.
  const navLabel = (key) => (!has(key) && hasLocalMyPageString(key) ? localMyPageString(locale, key) : t(key));
  const location = useLocation();
  // Lazy initialiser: read once, before the first paint, so the sidebar never
  // renders expanded and then snaps shut.
  const [preferCollapsed, setPreferCollapsed] = React.useState(readSidebarPref);
  // The Visit workspace's own rail state, `{ path, collapsed }`, set only when
  // the doctor toggles there. Derived rather than set by an effect, so a visit
  // never paints expanded and then snaps shut; keyed by path, so opening the
  // next patient starts collapsed again.
  const onVisit = isVisitPath(location.pathname);
  const [visitRail, setVisitRail] = React.useState(null);
  const collapsed = onVisit
    ? (visitRail?.path === location.pathname ? visitRail.collapsed : true)
    : preferCollapsed;
  React.useEffect(() => { if (!onVisit) setVisitRail(null); }, [onVisit]);

  const toggleCollapsed = React.useCallback(() => {
    if (onVisit) {
      setVisitRail({ path: location.pathname, collapsed: !collapsed });
      return;
    }
    setPreferCollapsed((c) => {
      writeSidebarPref(!c);
      return !c;
    });
  }, [onVisit, location.pathname, collapsed]);
  const { doctor } = useDoctor();
  const navigate = useAppNavigate();
  const balance = useWalletBalance(doctor?.id);

  // Clinic panel switch — see the button near the doctor card below.
  const { targets: clinicTargets, canSwitch: canSwitchClinic } = useClinicSwitch();

  // Live unread total from the app's one chat engine. Formatted and capped
  // through the SAME helper the Messages list uses, so «۹+» never means one
  // thing in the sidebar and another in the list. Empty string at zero — a
  // zero badge is a badge that should not exist.
  const unreadLabel = formatUnread(useChatUnread(), formatNumber);

  // The level from the one shared credit source, now the framework's — so the
  // doctor, dentist and clinic panels cannot disagree about what «کم» means.
  const balanceLevel = creditLevel(balance);
  // Buying credit is offered only where the deployment sells it and this panel
  // has a rail for its price (hooks/useCreditPurchase.js,
  // docs/platform-money/CONTRACT.md); elsewhere nothing here offers it, and the
  // balance and the way to its history stay.
  const { canBuy: canBuyCredit } = useCreditPurchase();
  // /credits has no nav item (entered via the balance chip) — nothing highlights there.
  const matchedNav = NAV_ITEMS.find(item => location.pathname.startsWith('/' + item.id))?.id;
  const currentScreen = matchedNav || (location.pathname.startsWith('/credits') ? null : 'reception');

  // Same leave-visit guard the nav items use — the chip navigates like a nav item.
  const guardedNavigate = (id) => {
    if (window.__parsina_visit_guard) {
      window.__parsina_visit_guard(() => navigate(id));
    } else {
      navigate(id);
    }
  };

  // Leaving for the clinic panel is a navigation OUT of the app, so it goes
  // through the same leave-visit guard the nav items use — an unsaved encounter
  // must prompt here exactly as it does for a click on «پذیرش». The guard is
  // the app's one confirmation point (`window.__parsina_visit_guard`); without
  // it this button would be the single way to lose a visit silently.
  const goToClinicPanel = () => {
    const go = () => enterClinicPanel(clinicTargets.length === 1 ? clinicTargets[0] : undefined);
    if (window.__parsina_visit_guard) {
      window.__parsina_visit_guard(go);
    } else {
      go();
    }
  };

  // The clinic's name only when there is exactly one — with several, naming one
  // of them would claim the switch lands there, and where it lands is the clinic
  // panel's own decision, not this app's.
  const clinicSwitchSubtitle = clinicTargets.length === 1 ? (clinicTargets[0].name || '') : '';

  // Labels/text fade in/out — they live in the DOM always to keep transition smooth
  const fade = {
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 0.22s ease',
    pointerEvents: collapsed ? 'none' : 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };

  return (
    // Outer: RTL — sidebar (first child) appears on the RIGHT.
    // `height: 100%`, not `100vh`: the shell owns the viewport now, because the
    // connectivity bar has to sit ABOVE the sidebar and push it down rather than
    // float over it. Overlaying would cover each screen's page header — which on
    // the visit screen is the patient's name, not something to hide behind an
    // infrastructure notice.
    // «منشی هوشمند» (PM-0517) wraps the shell: its record button sits at the foot of
    // the rail and what it brings back outlives any one screen.
    <SecretaryProvider>
    <div style={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>

      {/* ── Sidebar ── */}
      <div data-testid="app-sidebar" data-collapsed={collapsed ? 'true' : 'false'} style={{
        width: collapsed ? MINI : FULL,
        flexShrink: 0,
        background: GRADIENT_SIDEBAR,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
 
        // RTL clips from the LEFT when shrinking, so RIGHT side is always safe
        transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
        zIndex: 10,
      }}>

        {/* ── Header ──
            In RTL flex: items flow RIGHT → LEFT.
            Toggle button is FIRST → sits on the far RIGHT → never clipped. */}
        <div style={{
          height: 68,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}>

          {/* Icon column — fixed MINI width, anchored to the right → always visible */}
          <div style={{ width: MINI, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <button
              onClick={toggleCollapsed}
              data-testid="sidebar-toggle"
              aria-expanded={!collapsed}
              title={collapsed ? t('nav.sidebar.expand') : t('nav.sidebar.collapse')}
              style={{
                width: 36, height: 36,
                background: 'rgba(255,255,255,0.08)',
                border: 'none', borderRadius: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                color: 'rgba(255,255,255,0.6)',
                transition: 'background 0.18s, color 0.18s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.16)'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"
                style={{
                  transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
                  // The glyph points at the sidebar's own edge, so it mirrors
                  // with the layout as well as rotating with the toggle.
                  transform: `scaleX(var(--dir-flip, 1)) rotate(${collapsed ? 0 : 180}deg)`,
                }}>
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>
          </div>

          {/* Brand — fades out, sits to the LEFT of the toggle (gets clipped, which is fine) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingInlineStart: 4, paddingInlineEnd: 8, ...fade }}>
            <img src={brandAsset('logoWhite')} alt={t('nav.brand.name')} style={{ height: '2.4rem', width: 'auto', objectFit: 'contain', flexShrink: 0 }} />
            <div>
              <div style={{ color: 'white', fontSize: '1.0625rem', fontWeight: 800, whiteSpace: 'nowrap' }}>{t('nav.brand.name')}</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6875rem', whiteSpace: 'nowrap' }}>{t('nav.brand.panel')}</div>
            </div>
          </div>
        </div>

        {/* ── Nav items ──
            Each row: [icon RIGHT, always visible] [label LEFT, fades]
            Icon is FIRST in RTL → rightmost → safe from clipping. */}
        <nav style={{ flex: 1, paddingTop: 10, overflowY: 'auto', overflowX: 'hidden' }}>
          {NAV_ITEMS.map(item => {
            const active = currentScreen === item.id;
            return (
              <button
                key={item.id}
                onClick={() => guardedNavigate(item.id)}
                title={collapsed ? navLabel(item.labelKey) : undefined}
                style={{
                  // Rounded pill inset 10px from the sidebar edges. The icon cell is
                  // 48px, so the icon's optical center stays at 34px from the right
                  // edge — identical in collapsed (68px) and expanded (260px) states.
                  width: 'calc(100% - 20px)', margin: '2px 10px',
                  display: 'flex', alignItems: 'center',
                  padding: 0, border: 'none', borderRadius: 10,
                  background: active ? C.navy : 'transparent',
                  boxShadow: active ? '0 2px 8px rgba(0,0,0,0.22)' : 'none',
                  cursor: 'pointer',
                  transition: 'background 0.18s, box-shadow 0.18s',
                  fontFamily: 'var(--app-font)',
 
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Icon — first in RTL → rightmost → NEVER clipped */}
                <span style={{
                  width: MINI - 20, // pill is inset 10px each side of the MINI column
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '11px 0',
                  color: active ? 'white' : 'rgba(255,255,255,0.55)',
                  position: 'relative', // anchor for the unread chip below
                }}>
                  {item.icon}
                  {/* Anchored to the ICON, not the label: the label fades out at
                      the 68px mini-collapse and the count has to survive it.
                      Teal, not navy — the rule is "accent, never red", and the
                      sidebar's own surface IS navy, so a navy chip is invisible.
                      The count is already localised and capped. */}
                  {item.badge === 'chat' && unreadLabel && (
                    <span
                      data-testid="nav-messages-unread"
                      aria-label={t('messages.unreadBadge', { count: unreadLabel })}
                      style={{
                        position: 'absolute', top: 4, insetInlineEnd: 2,
                        minWidth: 16, height: 16, padding: '0 4px',
                        borderRadius: 8, background: C.teal, color: '#fff',
                        fontSize: '0.625rem', fontWeight: 800, lineHeight: '16px',
                        textAlign: 'center', pointerEvents: 'none',
                      }}
                    >
                      {unreadLabel}
                    </span>
                  )}
                </span>

                {/* Label — fades, sits to the left, gets clipped when collapsing */}
                <span style={{
                  ...fade,
                  color: active ? 'white' : 'rgba(255,255,255,0.55)',
                  fontSize: '0.875rem',
                  fontWeight: active ? 700 : 500,
                }}>
                  {navLabel(item.labelKey)}
                </span>
              </button>
            );
          })}
        </nav>

        {/* ── «منشی هوشمند» ── the record button, above the account card, as in the
            clinic panel (PM-0517). F1 anywhere does the same. */}
        <SecretaryRecorder collapsed={collapsed} iconCell={MINI - 20} />

        {/* ── AccountCard ──
            The پارسه balance, who is signed in, and every account-level
            destination, in ONE block from the kit. It replaces three separate
            things that used to sit here: a credits card, a full-width
            «رفتن به پنل کلینیک» button, and a doctor card ending in a bare
            16px sign-out glyph.

            Everything in `items` is an ACCOUNT-level action, which is why none
            of it belongs in NAV_ITEMS: these leave the current screen (and, for
            two of them, the SPA), so they must never take part in the
            active-item highlight.

            «رفتن به پنل کلینیک» is absent — not disabled — when this account has
            no clinic it may enter. A disabled row would advertise a panel the
            doctor cannot reach and invite the "why is this greyed out" question
            that has no answer inside this app.

            One row even for a doctor with several clinics, deliberately: the
            clinic panel resolves WHICH clinic itself — from the clinic it is
            asked for, else the clinics that account owns or staffs, asked of
            clinicmanagement. It is NOT a token claim (no realm mapper mints one),
            so naming one here would claim a landing the destination cannot honour. No token travels either — both panels use the same
            Keycloak client on the same realm, so the clinic app runs its own
            PKCE flow against the live SSO session and signs in without a second
            prompt. */}
        <AccountCard
          data-testid="account-card"
          collapsed={collapsed}
          menuLabel={t('nav.account.menuLabel')}
          user={{
            name: doctor?.name || '...',
            subtitle: doctor?.specialty || doctor?.email || '',
            avatarUrl: doctor?.avatar || undefined,
          }}
          credit={balance === null ? null : {
            // A STRING the app has already localised — the kit renders digits
            // verbatim, exactly as it does BottomNav's badge.
            amount: fmtCoins(balance, formatNumber),
            level: balanceLevel,
            unit: t('common.units.coin'),
            label: t('nav.credits.label'),
            levelLabel: balanceLevel ? t(CREDIT_LEVEL_META[balanceLevel].shortLabelKey) : '',
            onClick: () => guardedNavigate('credits'),
          }}
          onTopUp={canBuyCredit ? () => guardedNavigate('credits') : undefined}
          topUpLabel={canBuyCredit ? t('nav.credits.topUp') : undefined}
          items={[
            {
              key: 'account',
              // The identity is not this panel's to write — one account backs
              // every Parsina panel, so an editor here would be a second writer
              // against the same person. A plain same-tab <a>: same realm, same
              // Keycloak client, so the account app signs in silently.
              label: t('nav.account.parsina'),
              description: t('nav.account.parsinaHint'),
              icon: ICON.account,
              href: accountUrl('/'),
              external: true,
              'data-testid': 'sidebar-account',
            },
            // No «تنظیمات حساب» row: it is a visible nav destination already, and
            // a menu copy of a row one press away in the rail is noise. What
            // belongs here is what the nav deliberately does NOT carry —
            // «اعتبار هوشمند» is entered from the balance card above, not from a
            // nav item, so its top-up earns a row.
            canBuyCredit && balance !== null && {
              key: 'credits',
              label: t('nav.credits.topUp'),
              icon: ICON.coin,
              onClick: () => guardedNavigate('credits'),
            },
            canSwitchClinic && {
              key: 'clinic',
              label: t('nav.clinicSwitch.label'),
              description: clinicSwitchSubtitle,
              icon: ICON.clinic,
              onClick: goToClinicPanel,
              'data-testid': 'switch-to-clinic',
            },
            {
              key: 'logout',
              label: t('nav.sidebar.logout'),
              icon: ICON.logout,
              tone: 'destructive',
              separatorBefore: true,
              onClick: () => logout(),
            },
          ].filter(Boolean)}
        />

      </div>

      {/* ── Page content (LEFT side in RTL) ── */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {children}
      </div>
    </div>
    <SecretaryHost sidebarWidth={collapsed ? MINI : FULL} />
    </SecretaryProvider>
  );
};

export default WebLayout;
